import { useState } from "react";
import {
  Bot, Sparkles, Layers, Check, GitBranch, Lock, Loader2, X as XIcon, ExternalLink,
  GitMerge, GitCommit, GitPullRequest, AlertTriangle, Circle, FileCode, Pencil, Play, Square, Plus,
} from "lucide-react";

const ROLE_LANES = [
  { id: "contracts", label: "Contracts", icon: FileCode, hint: "Shared interfaces & types" },
  { id: "impl", label: "Coders", icon: Pencil, hint: "Feature agents, each on its own branch" },
  { id: "provision", label: "Provision", icon: Layers, hint: "Create resources — approval-gated" },
  { id: "deploy", label: "Deploy", icon: Play, hint: "Ship & live smoke-test — approval-gated" },
];

function mapChipStatus(s) {
  if (s === "ready") return "queued";
  return s || "idle";
}

// Canonical chip → Board-card mapper. Chips arrive from the durable substrate
// (bootstrap hydrate, beastmode launch, chip polling, maintainer proposals);
// the Board owns the card shape, so the mapping lives here — one copy, not four.
export function toCard(c) {
  return {
    versionId: c.version_id, branch: c.branch, title: c.title, taskId: c.task_id,
    sessionId: c.session_id, chipId: c.id, status: mapChipStatus(c.status),
    prUrl: c.pr_url, pushed: !!c.pr_url, verification: c.verification,
    role: c.role || "impl", requiresApproval: !!c.requires_approval, acceptance: c.acceptance, metrics: c.metrics,
  };
}

export function Board({ cards, parseNotes, versions, githubLinked, onOpen, onMerge, onDiscard, onStop, onNew, onPush, onPR, onApprove, onClear,
                onRunMaintainer, proposal, onApprovePlan, onDismissProposal, onAddChip }) {
  const [adding, setAdding] = useState(false);
  const [chipTitle, setChipTitle] = useState("");
  const items = cards.length
    ? cards
    : versions.filter((v) => !v.is_main).map((v) => ({ versionId: v.id, branch: v.branch, title: v.name, status: "idle", role: "impl" }));
  const lanes = ROLE_LANES
    .map((l) => ({ ...l, cards: items.filter((c) => (c.role || "impl") === l.id) }))
    .filter((l) => l.cards.length);
  return (
    <div className="flex-1 overflow-y-auto cadence-scroll px-8 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="label-caps mb-1">Virtual dev team</p>
            <h2 className="font-heading font-800 text-2xl text-gray-900">Agent board</h2>
            <p className="text-sm text-gray-500 mt-1">Planner → Coders → Deployer. Each card is an agent on its own branch; resource-creating and go-live steps wait for your approval.</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="maintainer-run" onClick={onRunMaintainer}
              className="text-sm px-4 py-2.5 rounded-lg border border-gray-900 text-gray-900 hover:bg-gray-50 inline-flex items-center gap-2 font-medium">
              <Bot className="w-4 h-4" /> Run Maintainer
            </button>
            <button data-testid="board-new-build" onClick={onNew}
              className="bg-black text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-900 inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> New parallel build
            </button>
            {cards.length > 0 && (
              <button data-testid="board-add-chip" onClick={() => setAdding((a) => !a)}
                className="text-sm px-4 py-2.5 rounded-lg border border-gray-900 text-gray-900 hover:bg-gray-50 inline-flex items-center gap-2 font-medium">
                <Plus className="w-4 h-4" /> Add chip
              </button>
            )}
            {cards.length > 0 && (
              <button data-testid="board-clear" onClick={onClear}
                className="text-xs px-3 py-2.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                Clear
              </button>
            )}
          </div>
        </div>
        {adding && (
          <form data-testid="board-add-chip-form"
            onSubmit={(e) => {
              e.preventDefault();
              const t = chipTitle.trim();
              if (!t) return;
              onAddChip?.(t);
              setChipTitle("");
              setAdding(false);
            }}
            className="mb-6 flex items-center gap-2">
            <input data-testid="board-add-chip-input" autoFocus value={chipTitle}
              onChange={(e) => setChipTitle(e.target.value)} placeholder="New chip title…"
              className="flex-1 max-w-md text-sm px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900/10" />
            <button type="submit" disabled={!chipTitle.trim()}
              className="text-sm px-4 py-2 rounded-lg bg-black text-white hover:bg-gray-900 disabled:opacity-40">Add</button>
            <button type="button" onClick={() => { setAdding(false); setChipTitle(""); }}
              className="text-sm px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
          </form>
        )}
        {parseNotes?.length > 0 && (
          <div data-testid="plan-repair-notice" className="mb-6 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="label-caps text-amber-700 mb-0.5">Planner output was auto-repaired</p>
                <p className="text-[13px] text-gray-600">The planner's JSON was malformed and the host repaired it before building. Review the plan — a repaired plan can be subtly wrong.</p>
                <ul className="mt-2 text-[12px] text-amber-800 list-disc pl-4 space-y-0.5">
                  {parseNotes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </div>
            </div>
          </div>
        )}
        {proposal && (
          <div data-testid="maintainer-proposal" className="mb-6 rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
            <div className="flex items-start gap-3">
              <Bot className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="label-caps text-indigo-700 mb-0.5">Proposed by Maintainer · awaiting your approval</p>
                <h3 className="font-heading font-700 text-[15px] text-gray-900 break-words">{proposal.goal}</h3>
                {proposal.rationale && <p className="text-[13px] text-gray-600 mt-1 break-words">{proposal.rationale}</p>}
                <div className="flex items-center gap-2 mt-3">
                  <button data-testid="maintainer-approve" onClick={onApprovePlan}
                    className="text-xs px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" /> Approve plan &amp; start agents
                  </button>
                  <button data-testid="maintainer-dismiss" onClick={onDismissProposal}
                    className="text-xs px-3 py-2 rounded-md border border-gray-200 text-gray-600 hover:bg-white">
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {lanes.length === 0 ? (
          <div className="border border-dashed border-gray-300 rounded-xl py-16 text-center text-gray-400">
            <Layers className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <div className="text-sm">No parallel branches yet. Start a build to spin up your dev team.</div>
          </div>
        ) : (
          <div className="space-y-7">
            {lanes.map((lane) => (
              <div key={lane.id} data-testid={`board-lane-${lane.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <lane.icon className="w-4 h-4 text-gray-500" />
                  <h3 className="font-heading font-700 text-sm text-gray-900">{lane.label}</h3>
                  <span className="text-[11px] text-gray-400">{lane.cards.length}</span>
                  <span className="text-[11px] text-gray-400 hidden sm:inline">· {lane.hint}</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {lane.cards.map((c) => <BoardCard key={c.versionId || c.chipId} c={c} githubLinked={githubLinked}
                    onOpen={onOpen} onMerge={onMerge} onDiscard={onDiscard} onStop={onStop} onPush={onPush} onPR={onPR} onApprove={onApprove} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VerificationLine({ v }) {
  if (!v || !v.state || v.state === "none") return null;
  const map = {
    passed: { Icon: Check, cls: "text-emerald-600", t: "Tests passed" },
    skipped: { Icon: Circle, cls: "text-gray-400", t: "No tests found" },
    failed: { Icon: XIcon, cls: "text-red-600", t: "Tests failed" },
    error: { Icon: XIcon, cls: "text-red-600", t: "Verify error" },
    blocked: { Icon: Circle, cls: "text-gray-400", t: "Blocked by dependency" },
  }[v.state] || { Icon: Circle, cls: "text-gray-400", t: v.state };
  const { Icon, cls, t } = map;
  return (
    <div data-testid="board-verification" className="mt-2 flex items-center gap-1.5 text-[11px] min-w-0">
      <Icon className={`w-3.5 h-3.5 shrink-0 ${cls}`} />
      <span className={cls}>{t}</span>
      {v.command && <span className="font-mono text-gray-400 truncate">· {v.command}</span>}
    </div>
  );
}

function BoardCard({ c, githubLinked, onOpen, onMerge, onDiscard, onStop, onPush, onPR, onApprove }) {
  const badge = {
    queued: { t: "Queued", cls: "bg-indigo-50 text-indigo-700" },
    working: { t: "Working", cls: "bg-blue-50 text-blue-700", spin: true },
    blocked: { t: "Blocked", cls: "bg-gray-100 text-gray-500" },
    verifying: { t: "Verifying", cls: "bg-violet-50 text-violet-700", spin: true },
    awaiting_approval: { t: "Awaiting approval", cls: "bg-amber-50 text-amber-700" },
    ready_to_merge: { t: "Ready to merge", cls: "bg-emerald-50 text-emerald-700" },
    ready: { t: "Ready", cls: "bg-emerald-50 text-emerald-700" },
    merged: { t: "Merged", cls: "bg-gray-100 text-gray-500" },
    failed: { t: "Failed", cls: "bg-red-50 text-red-700" },
    idle: { t: "Idle", cls: "bg-gray-100 text-gray-500" },
  }[c.status] || { t: c.status, cls: "bg-gray-100 text-gray-500" };
  const canMerge = c.status === "ready_to_merge";
  const canSync = !["working", "verifying", "queued", "blocked"].includes(c.status);
  return (
    <div data-testid="board-card" className="border border-gray-200 rounded-xl p-4 bg-white flex flex-col min-w-0 hover:shadow-md transition-shadow animate-fade-up">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-500 min-w-0">
          <GitBranch className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{c.branch}</span>
          {c.requiresApproval && <Lock className="w-3 h-3 shrink-0 text-amber-500" />}
        </span>
        <span className={`text-[11px] font-600 px-2 py-0.5 rounded-md inline-flex items-center gap-1 shrink-0 whitespace-nowrap ${badge.cls}`}>
          {badge.spin && <Loader2 className="w-3 h-3 animate-spin" />}{badge.t}
        </span>
      </div>
      <div className="font-heading font-700 text-[15px] text-gray-900 break-words">{c.title}</div>
      {c.acceptance && <div className="text-[11px] text-gray-400 mt-1 truncate" title={c.acceptance}>{c.acceptance}</div>}
      <VerificationLine v={c.verification} />
      {c.verification?.review && (
        <div data-testid="board-review" className="mt-1.5 text-[11px] inline-flex items-center gap-1.5 min-w-0">
          <Sparkles className={`w-3 h-3 shrink-0 ${c.verification.review.verdict === "approve" ? "text-emerald-600" : "text-amber-600"}`} />
          <span className={`truncate ${c.verification.review.verdict === "approve" ? "text-emerald-600" : "text-amber-600"}`}>
            Review: {c.verification.review.verdict === "approve" ? "approved" : "changes requested"}{c.verification.review.flagged ? " · needs attention" : ""}
          </span>
        </div>
      )}
      {c.verification?.url && (
        <a href={c.verification.url} target="_blank" rel="noopener noreferrer" data-testid="board-live-url"
          className="mt-1.5 text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1 min-w-0">
          <ExternalLink className="w-3 h-3 shrink-0" /> <span className="truncate">Live: {c.verification.url}</span>
        </a>
      )}
      {c.verification?.scope && !c.verification.scope.ok && (
        <div data-testid="board-scope-warning" className="mt-1.5 text-[11px] text-amber-600 inline-flex items-center gap-1.5 min-w-0">
          <XIcon className="w-3 h-3 shrink-0" />
          <span className="truncate">{c.verification.scope.out_of_scope?.length || 0} file(s) outside declared scope</span>
        </div>
      )}
      {c.layout_check && !c.layout_check.ok && (
        <div data-testid="board-layout-warning" className="mt-1.5 text-[11px] text-amber-600 inline-flex items-center gap-1.5 min-w-0">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          <span className="truncate">{c.layout_check.out_of_layout?.length || 0} path(s) outside project layout</span>
        </div>
      )}
      {c.verification?.secrets?.length > 0 && (
        <div data-testid="board-secrets" className="mt-1.5 text-[11px] text-gray-500 inline-flex items-center gap-1.5 min-w-0">
          <Lock className="w-3 h-3 shrink-0" /> <span className="truncate">secrets: {c.verification.secrets.join(", ")}</span>
        </div>
      )}
      {c.metrics && (
        <div data-testid="board-metrics" className="mt-1.5 text-[11px] text-gray-400">
          {c.metrics.attempts} attempt{c.metrics.attempts === 1 ? "" : "s"} · {c.metrics.duration_s}s
        </div>
      )}
      {c.status === "awaiting_approval" && (
        <button data-testid="board-approve" onClick={() => onApprove(c)}
          className="mt-2 w-full text-xs px-2.5 py-1.5 rounded-md bg-amber-500 text-white hover:bg-amber-600 inline-flex items-center justify-center gap-1.5">
          <Lock className="w-3 h-3" /> Approve &amp; proceed
        </button>
      )}
      <div className="flex items-center gap-2 mt-4 flex-wrap">
        <button data-testid="board-open" onClick={() => onOpen(c.versionId)} className="text-xs px-2.5 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50">Open</button>
        <button data-testid="board-merge" onClick={() => onMerge(c)} disabled={!canMerge}
          className="text-xs px-2.5 py-1.5 rounded-md bg-gray-900 text-white hover:bg-black disabled:opacity-40 inline-flex items-center gap-1"><GitMerge className="w-3 h-3" /> Merge</button>
        {c.status === "working" && c.taskId && (
          <button data-testid="board-stop" onClick={() => onStop(c)} title="Stop this agent"
            className="text-xs px-2.5 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"><Square className="w-3 h-3" /> Stop</button>
        )}
        <button data-testid="board-discard" onClick={() => onDiscard(c)} className="text-xs px-2 py-1.5 rounded-md text-gray-400 hover:text-red-600 ml-auto"><XIcon className="w-3.5 h-3.5" /></button>
      </div>
      {githubLinked && (
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100 flex-wrap">
          <button data-testid="board-push" onClick={() => onPush(c)} disabled={!canSync}
            className="text-xs px-2.5 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 inline-flex items-center gap-1">
            <GitCommit className="w-3 h-3" /> Push{c.pushed ? "ed" : ""}
          </button>
          <button data-testid="board-open-pr" onClick={() => onPR(c)} disabled={!canSync}
            className="text-xs px-2.5 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 inline-flex items-center gap-1">
            <GitPullRequest className="w-3 h-3" /> {c.prUrl ? "Re-open PR" : "Open PR"}
          </button>
          {c.prUrl && (
            <a href={c.prUrl} target="_blank" rel="noopener noreferrer" data-testid="board-view-pr"
              className="text-xs px-2.5 py-1.5 rounded-md text-blue-600 hover:bg-blue-50 inline-flex items-center gap-1 whitespace-nowrap">
              <ExternalLink className="w-3 h-3" /> View PR
            </a>
          )}
        </div>
      )}
    </div>
  );
}
